import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import MainLayout from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  FileText, 
  Clock, 
  Euro,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ChevronDown,
  ChevronRight,
  Calendar,
  Upload,
  Download,
  Award,
  TrendingUp,
  Send,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';

const MemberReportsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedReport, setSelectedReport] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [expandedReports, setExpandedReports] = useState({});

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const res = await api.get('/monthly-reports/member');
      setReports(res.data || []);
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Erreur lors du chargement des rapports');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadInvoice = async (reportId, file) => {
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await api.post(`/monthly-reports/${reportId}/upload-invoice`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Facture téléchargée avec succès');
      fetchReports();
      setShowUploadDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement');
    } finally {
      setUploading(false);
    }
  };

  const toggleExpanded = (reportId) => {
    setExpandedReports(prev => ({
      ...prev,
      [reportId]: !prev[reportId]
    }));
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: { class: 'bg-gray-100 text-gray-700', label: 'Brouillon' },
      pending_validation: { class: 'bg-yellow-100 text-yellow-700', label: 'En attente validation' },
      validated: { class: 'bg-blue-100 text-blue-700', label: 'Validé' },
      pending_invoice: { class: 'bg-orange-100 text-orange-700', label: 'Facture à joindre' },
      paid: { class: 'bg-green-100 text-green-700', label: 'Payé' },
      rejected: { class: 'bg-red-100 text-red-700', label: 'Rejeté' }
    };
    const style = styles[status] || styles.draft;
    return <Badge className={cn('font-medium', style.class)}>{style.label}</Badge>;
  };

  const filteredReports = reports.filter(report => {
    if (activeTab === 'pending') {
      return ['draft', 'pending_validation', 'pending_invoice'].includes(report.status);
    }
    if (activeTab === 'validated') {
      return ['validated', 'paid'].includes(report.status);
    }
    return true;
  });

  // Calculate stats
  const stats = {
    totalReports: reports.length,
    pendingValidation: reports.filter(r => r.status === 'pending_validation').length,
    pendingInvoice: reports.filter(r => r.status === 'pending_invoice' || r.status === 'validated').length,
    totalEarned: reports.filter(r => r.status === 'paid').reduce((acc, r) => acc + (r.total_amount || 0), 0),
    totalHours: reports.reduce((acc, r) => acc + (r.total_hours || 0), 0)
  };

  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#1a365d] flex items-center gap-3" data-testid="reports-title">
              <FileText className="w-8 h-8 text-blue-600" />
              Mes Rapports d'Activité
            </h1>
            <p className="text-[#64748b] mt-1">
              Suivez vos rapports mensuels et vos gains
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total rapports</p>
                  <p className="text-2xl font-bold">{stats.totalReports}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Clock className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">En attente</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.pendingValidation}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Upload className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Facture à joindre</p>
                  <p className="text-2xl font-bold text-orange-600">{stats.pendingInvoice}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Euro className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total perçu</p>
                  <p className="text-lg font-bold text-green-600">
                    {stats.totalEarned.toLocaleString('fr-FR')}€
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="pending" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              En cours
              {stats.pendingValidation + stats.pendingInvoice > 0 && (
                <Badge variant="destructive" className="ml-1 text-xs">
                  {stats.pendingValidation + stats.pendingInvoice}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="validated" className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              Validés
            </TabsTrigger>
            <TabsTrigger value="all" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Tous
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab}>
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : filteredReports.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Aucun rapport</h3>
                  <p className="text-muted-foreground">
                    {activeTab === 'pending' 
                      ? 'Vous n\'avez pas de rapport en attente'
                      : activeTab === 'validated'
                        ? 'Aucun rapport validé pour le moment'
                        : 'Vos rapports d\'activité apparaîtront ici'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredReports.map((report) => (
                  <Card key={report.id} className="overflow-hidden">
                    <div 
                      className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => toggleExpanded(report.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-slate-100 rounded-lg">
                            <Calendar className="w-5 h-5 text-slate-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-[#1a365d]">
                              {report.month_label || `Rapport ${report.month}/${report.year}`}
                            </h3>
                            <p className="text-sm text-slate-500">
                              {report.contract_name || 'Contrat principal'}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-mono font-bold text-lg">
                              {(report.total_amount || 0).toLocaleString('fr-FR')}€
                            </p>
                            <p className="text-xs text-slate-500">
                              {(report.total_hours || 0).toFixed(1)}h travaillées
                            </p>
                          </div>
                          {getStatusBadge(report.status)}
                          {expandedReports[report.id] ? (
                            <ChevronDown className="w-5 h-5 text-slate-400" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {expandedReports[report.id] && (
                      <div className="border-t p-4 bg-slate-50">
                        {/* Report details */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div className="p-3 bg-white rounded-lg border">
                            <p className="text-xs text-slate-500">Heures travaillées</p>
                            <p className="text-lg font-bold">{(report.total_hours || 0).toFixed(1)}h</p>
                          </div>
                          <div className="p-3 bg-white rounded-lg border">
                            <p className="text-xs text-slate-500">Taux horaire</p>
                            <p className="text-lg font-bold">{report.hourly_rate || 0}€/h</p>
                          </div>
                          <div className="p-3 bg-white rounded-lg border">
                            <p className="text-xs text-slate-500">Prime</p>
                            <p className="text-lg font-bold text-green-600">
                              +{(report.bonus_amount || 0).toLocaleString('fr-FR')}€
                            </p>
                          </div>
                        </div>
                        
                        {/* Tasks breakdown if available */}
                        {report.tasks && report.tasks.length > 0 && (
                          <div className="mb-4">
                            <h4 className="text-sm font-medium mb-2">Détail des tâches</h4>
                            <div className="space-y-2">
                              {report.tasks.slice(0, 5).map((task, idx) => (
                                <div key={idx} className="flex justify-between items-center p-2 bg-white rounded border text-sm">
                                  <span>{task.name || task.title}</span>
                                  <span className="font-mono">{(task.hours || 0).toFixed(1)}h</span>
                                </div>
                              ))}
                              {report.tasks.length > 5 && (
                                <p className="text-xs text-slate-500 text-center">
                                  +{report.tasks.length - 5} autres tâches
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Agency notes */}
                        {report.agency_notes && (
                          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 mb-4">
                            <p className="text-xs text-blue-600 font-medium mb-1">Note de l'agence</p>
                            <p className="text-sm text-blue-800">{report.agency_notes}</p>
                          </div>
                        )}
                        
                        {/* Actions */}
                        <div className="flex flex-wrap gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setSelectedReport(report);
                              setShowDetailDialog(true);
                            }}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            Voir détails
                          </Button>
                          
                          {(report.status === 'validated' || report.status === 'pending_invoice' || report.status === 'pending_payment') && !report.member_invoice_url && (
                            <Button 
                              size="sm"
                              className="bg-orange-500 hover:bg-orange-600"
                              onClick={() => {
                                setSelectedReport(report);
                                setShowUploadDialog(true);
                              }}
                            >
                              <Upload className="w-4 h-4 mr-2" />
                              Joindre ma facture
                            </Button>
                          )}
                          
                          {report.member_invoice_url && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              asChild
                            >
                              <a href={`${process.env.REACT_APP_BACKEND_URL}${report.member_invoice_url}`} target="_blank" rel="noopener noreferrer">
                                <Download className="w-4 h-4 mr-2" />
                                Ma facture
                              </a>
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Détails du rapport
              </DialogTitle>
            </DialogHeader>
            
            {selectedReport && (
              <div className="space-y-4 py-4">
                <div className="flex justify-between items-start p-4 bg-slate-50 rounded-xl">
                  <div>
                    <h3 className="font-semibold text-lg">{selectedReport.month_label}</h3>
                    <p className="text-sm text-slate-500">{selectedReport.contract_name}</p>
                  </div>
                  {getStatusBadge(selectedReport.status)}
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 border rounded-xl">
                    <p className="text-sm text-slate-500">Total heures</p>
                    <p className="text-2xl font-bold">{(selectedReport.total_hours || 0).toFixed(1)}h</p>
                  </div>
                  <div className="p-4 border rounded-xl">
                    <p className="text-sm text-slate-500">Montant total</p>
                    <p className="text-2xl font-bold text-green-600">
                      {(selectedReport.total_amount || 0).toLocaleString('fr-FR')}€
                    </p>
                  </div>
                </div>
                
                {selectedReport.bonus_amount > 0 && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Award className="w-5 h-5 text-green-600" />
                      <p className="font-medium text-green-800">Prime accordée</p>
                    </div>
                    <p className="text-2xl font-bold text-green-600">
                      +{(selectedReport.bonus_amount || 0).toLocaleString('fr-FR')}€
                    </p>
                    {selectedReport.bonus_reason && (
                      <p className="text-sm text-green-700 mt-1">{selectedReport.bonus_reason}</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Upload Invoice Dialog */}
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-orange-600" />
                Joindre ma facture
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4">
              <p className="text-sm text-slate-600 mb-4">
                Téléchargez votre facture au format PDF pour ce rapport. 
                Une fois votre facture validée, le paiement sera initié.
              </p>
              
              <Label htmlFor="invoice-file">Fichier PDF</Label>
              <Input
                id="invoice-file"
                type="file"
                accept=".pdf"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file && selectedReport) {
                    handleUploadInvoice(selectedReport.id, file);
                  }
                }}
                disabled={uploading}
                className="mt-2"
              />
              
              {uploading && (
                <div className="flex items-center gap-2 mt-4 text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Téléchargement en cours...
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default MemberReportsPage;
